import { Field, Form, Formik, FormikState } from 'formik'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { useGetDtsPlace, usePatchDtsPlace, usePlace } from 'hooks/usePlaces'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import GoBackButton from 'components/GoBackButton/GoBackButton'
import { useAlert } from 'providers/AlertProvider'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { ResponsiveBoxWrapper } from 'components/Layout/Layout.styled'
import { BlueSwitch } from './Place.styled'
import { PlaceSecondarySidebar } from './PlaceSecondarySidebar'

type ValueProps = {
  deliveryStore: boolean
  activeForOrdering: boolean
  collectionStore: boolean
  usesShopkeeper: boolean
  sendDeliveryEstimates: boolean
}

export const PlaceFulfilment = () => {
  const { t } = useTranslation()
  const { placeId } = useParams()
  const {
    isLoading: isLoadingPlace,
    error: placeError,
    place,
  } = usePlace(placeId as string)
  const { isLoading, error, dtsPlace } = useGetDtsPlace(place.masterId)
  const { mutateAsync, isLoading: isLoadingRequest } = usePatchDtsPlace(
    placeId as string
  )
  const { setAlert } = useAlert()

  const [showOverlay, setShowOverlay] = useState(true)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const initialValues = {
    deliveryStore: dtsPlace.deliveryStore,
    activeForOrdering: dtsPlace.activeForOrdering,
    collectionStore: dtsPlace.collectionStore,
    usesShopkeeper: dtsPlace.usesShopkeeper,
    sendDeliveryEstimates: dtsPlace.sendDeliveryEstimates,
  }

  const validationSchema = yup.object({
    deliveryStore: yup.boolean().required(),
    activeForOrdering: yup.boolean().required(),
    collectionStore: yup.boolean().required(),
    usesShopkeeper: yup.boolean().required(),
    sendDeliveryEstimates: yup.boolean().required(),
  })

  const handleSubmit = async (
    values: ValueProps,
    resetForm: (nextState?: Partial<FormikState<ValueProps>>) => void
  ) => {
    const modifiedValues = {
      active_for_ordering: values.activeForOrdering,
      delivery_store: values.deliveryStore,
      collection_store: values.collectionStore,
      send_delivery_estimates: values.sendDeliveryEstimates,
      uses_shopkeeper: values.usesShopkeeper,
      store_guid: dtsPlace.guid,
    }
    await mutateAsync(modifiedValues, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
        resetForm({ values })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const fulfilmentServiceAvailability = [
    { key: 'activeForOrdering', value: t('placeFulfilment.activeForOrdering') },
    { key: 'deliveryStore', value: t('placeFulfilment.deliveryActive') },
    { key: 'collectionStore', value: t('placeFulfilment.collectionActive') },
  ]

  const workFlow = [
    { key: 'usesShopkeeper', value: t('placeFulfilment.naveoPickingActive') },
    { key: 'sendDeliveryEstimates', value: t('placeFulfilment.naveoLastMile') },
  ]

  if (isLoading || !place || isLoadingPlace || !dtsPlace)
    return <LoadingIndicator />
  if (error || placeError) return <h1>Error</h1>
  return (
    <Box sx={{ display: 'flex' }}>
      <PlaceSecondarySidebar
        placeId={placeId as string}
        placeName={place.name}
        placeMasterId={place.masterId}
      />
      <ResponsiveBoxWrapper showOverlay={showOverlay}>
        <Box maxWidth='500px'>
          {isMobile && <GoBackButton onClick={() => setShowOverlay(false)} />}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) =>
              handleSubmit(values, resetForm)
            }>
            {({ setFieldValue, values, isValid, dirty }) => (
              <Form>
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-between'>
                  {place && (
                    <Box
                      display='flex'
                      flexDirection='column'
                      justifyContent='space-around'
                      mt={3}>
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(3),
                          marginTop: 0,
                          color: theme.palette.text.secondary,
                        }}>
                        {t(
                          'placeFulfilment.fulfilmentServiceAvailability'
                        ).toUpperCase()}
                      </Typography>
                      {fulfilmentServiceAvailability.map((service) => {
                        const serviceKey = service.key
                        return (
                          <Box
                            key={serviceKey}
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            mb={theme.spacing(3)}
                            ml={theme.spacing(2)}>
                            <Typography variant='body2'>
                              {service.value}
                            </Typography>
                            <Field
                              name={serviceKey}
                              component={BlueSwitch}
                              value={
                                values[
                                  serviceKey as keyof Partial<ValueProps>
                                ] ?? false
                              }
                              checked={
                                values[serviceKey as keyof Partial<ValueProps>]
                              }
                              onChange={() => {
                                setFieldValue(
                                  serviceKey,
                                  !values[
                                    serviceKey as keyof Partial<ValueProps>
                                  ]
                                )
                              }}
                              aria-label={t('toggle', { value: serviceKey })}
                            />
                          </Box>
                        )
                      })}
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(3),
                          marginTop: 0,
                          color: theme.palette.text.secondary,
                        }}>
                        {t('placeFulfilment.workflow').toUpperCase()}
                      </Typography>
                      {workFlow.map((service) => {
                        const serviceKey = service.key
                        return (
                          <Box
                            key={serviceKey}
                            display='flex'
                            justifyContent='space-between'
                            alignItems='center'
                            mb={3}
                            ml={2}>
                            <Typography variant='body2'>
                              {service.value}
                            </Typography>
                            <Field
                              name={serviceKey}
                              component={BlueSwitch}
                              value={
                                values[
                                  serviceKey as keyof Partial<ValueProps>
                                ] ?? false
                              }
                              checked={
                                values[serviceKey as keyof Partial<ValueProps>]
                              }
                              onChange={() => {
                                setFieldValue(
                                  serviceKey,
                                  !values[
                                    serviceKey as keyof Partial<ValueProps>
                                  ]
                                )
                              }}
                              aria-label={t('toggle', { value: serviceKey })}
                            />
                          </Box>
                        )
                      })}
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'fixed',
                      bottom: isMobile ? '0px' : theme.spacing(2),
                      width: isMobile ? '92%' : '504px',
                      zIndex: 1000,
                    }}>
                    <SaveBtn
                      showOverlay={showOverlay}
                      dirty={dirty}
                      isValid={isValid}
                      isLoadingRequest={isLoadingRequest}
                    />
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </ResponsiveBoxWrapper>
    </Box>
  )
}
